import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { ArrowDown, ArrowUp } from 'lucide-react';
import CustomButton from '../../components/CustomButton';
import {
    useAddFaqMutation,
    useRemoveFaqMutation,
    useReorderFaqsMutation,
    useUpdateFaqMutation,
} from '../../api/account/accountApiSlice';
import { TenantFaqItem, TenantFaqText, TenantFaqs } from '../../api/account/accountApiTypes';
import { ProductCategory } from '../../api/productCategory/productCategoryApiSlice';
import { useTenantProductCategoriesQuery } from '../../hooks/useTenantProductCategoriesQuery';
import { enqueueErrorSnackbar, enqueueSuccessSnackbar } from '../../action/snackbarCommands';
import { errorMessageFrom } from '../../utils/errorMessage';
import {
    FaqDraft,
    MAX_FAQS_PER_SECTION,
    MAX_FAQS_TOTAL,
    MAX_FAQ_ANSWER_LENGTH,
    MAX_FAQ_QUESTION_LENGTH,
    draftFrom,
    faqDraftProblem,
    faqTextsOf,
    totalFaqs,
} from './tenantFaqRules';

const INPUT_CLASS = 'w-full rounded-[5px] border border-foreground/15 px-3 py-2 text-sm text-foreground'
    + ' bg-transparent';

interface Props {
    faqs?: TenantFaqs;
    /** Use cases the tenant offers — a category of any other is not shown. */
    services?: string[] | null;
    /** No writes — a member who is not the owner, or a master viewing another tenant. */
    readOnly: boolean;
}

/** One editable list: tenant-wide, or one product category. */
interface FaqList {
    /** `undefined` = tenant-wide. */
    category?: string;
    label: string;
    items: TenantFaqItem[];
}

/** What is open: the add form of a list, or the edit form of an item. */
type Editing = { kind: 'add', list: string } | { kind: 'edit', id: string } | null;

const listKey = (category?: string) => category ?? '';

const categoryLabel = (category: ProductCategory, language: string): string => {
    const title = category.langNames?.[language.slice(0, 2)]?.title;
    return title && title.trim() ? title.trim() : category.name;
};

/** The question in the admin's language, else the other one. */
const questionOf = (item: TenantFaqItem, language: string): string => {
    const preferred = language.startsWith('de') ? item.question.de : item.question.en;
    return preferred ?? item.question.de ?? item.question.en ?? '';
};

const FIELD_LABEL_KEYS: Record<keyof FaqDraft, string> = {
    questionDe: 'services.faqs_question_de',
    answerDe: 'services.faqs_answer_de',
    questionEn: 'services.faqs_question_en',
    answerEn: 'services.faqs_answer_en',
};

interface FormProps {
    initial: FaqDraft;
    busy: boolean;
    onSave: (question: TenantFaqText, answer: TenantFaqText) => Promise<boolean>;
    onCancel: () => void;
}

/** DE and EN, question and answer — one language is enough. */
const FaqForm = ({ initial, busy, onSave, onCancel }: FormProps) => {
    const { t } = useTranslation();
    const [draft, setDraft] = useState<FaqDraft>(initial);
    const problem = faqDraftProblem(draft);

    const field = (name: keyof FaqDraft, max: number, multiline: boolean) => {
        const id = `faq-${name}`;
        const label = t(FIELD_LABEL_KEYS[name]);
        const value = draft[name];
        const common = {
            id,
            value,
            maxLength: max,
            disabled: busy,
            className: INPUT_CLASS,
            onChange: (event: { target: { value: string } }) =>
                setDraft((prev) => ({ ...prev, [name]: event.target.value })),
        };
        return (
            <div className={'flex flex-col gap-1'}>
                <label htmlFor={id} className={'text-sm text-foreground/70'}>{label}</label>
                {multiline ? <textarea rows={4} {...common} /> : <input type={'text'} {...common} />}
                <span className={'text-xs text-foreground/60 self-end'}>
                    {t('services.faqs_chars', { count: value.length, max })}
                </span>
            </div>
        );
    };

    return (
        <div className={'flex flex-col gap-3 border border-foreground/15 rounded-[5px] p-3'}>
            {field('questionDe', MAX_FAQ_QUESTION_LENGTH, false)}
            {field('answerDe', MAX_FAQ_ANSWER_LENGTH, true)}
            {field('questionEn', MAX_FAQ_QUESTION_LENGTH, false)}
            {field('answerEn', MAX_FAQ_ANSWER_LENGTH, true)}
            {problem && (draft.questionDe || draft.answerDe || draft.questionEn || draft.answerEn) && (
                <p className={'text-sm text-foreground/70'} role={'alert'}>
                    {t(`api_errors.${problem}`)}
                </p>
            )}
            <div className={'flex flex-wrap gap-2'}>
                <CustomButton
                    title={t('common.save')}
                    onClick={async () => {
                        const texts = faqTextsOf(draft);
                        await onSave(texts.question, texts.answer);
                    }}
                    isFilled={false}
                    disabled={busy || problem !== null}
                    loading={busy}
                />
                <CustomButton title={t('common.cancel')} onClick={onCancel} isFilled={false} />
            </div>
        </div>
    );
};

/**
 * The tenant's own FAQ questions (ADR-0003 amendment 2026-10-06). kolula's
 * set per use case is fixed; the tenant adds questions tenant-wide or per
 * product category, and the end-user app shows them at the end of the
 * matching section.
 *
 * Questions of an archived category are kept but not shown — in the app or
 * here — and come back with the category. Deleting a category deletes its
 * questions with it (lead dev, 2026-10-08).
 *
 * Every write saves at once, like the terms above — the Services page has no
 * unsaved-changes guard (ADR-0005, 2026-09-12 amendment).
 */
const TenantFaqsSection = ({ faqs, services, readOnly }: Props) => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const { data: categories, isSuccess: categoriesLoaded } = useTenantProductCategoriesQuery();
    const [addFaq, { isLoading: adding }] = useAddFaqMutation();
    const [updateFaq, { isLoading: updating }] = useUpdateFaqMutation();
    const [removeFaq, { isLoading: removing }] = useRemoveFaqMutation();
    const [reorderFaqs, { isLoading: reordering }] = useReorderFaqsMutation();
    const [editing, setEditing] = useState<Editing>(null);
    const [confirmingRemove, setConfirmingRemove] = useState<string | null>(null);
    const busy = adding || updating || removing || reordering;

    const labelOfUseCase = (useCase: string) =>
        t(`service_type_selector.${useCase}`, { defaultValue: useCase });

    // Categories grouped by use case, in the tenant's service order. A use case
    // with one category is labelled by the use case alone — the tenant chose
    // no finer split, so the category name would only add noise.
    const categoryLists: FaqList[] = (services ?? []).flatMap((useCase) => {
        const ofUseCase = (categories ?? []).filter(
            (category) => category.useCase.toLowerCase() === useCase.toLowerCase() && !category.archivedAt,
        );
        return ofUseCase.map((category) => ({
            category: category.key,
            label: ofUseCase.length > 1
                ? `${labelOfUseCase(useCase)} · ${categoryLabel(category, i18n.language)}`
                : labelOfUseCase(useCase),
            items: faqs?.categories[category.key] ?? [],
        }));
    });
    const lists: FaqList[] = [
        { label: t('services.faqs_general'), items: faqs?.general ?? [] },
        ...categoryLists,
    ];
    const totalFull = totalFaqs(faqs) >= MAX_FAQS_TOTAL;
    // Questions of archived categories still count towards the limit but are
    // not listed here; say so, or a full limit would have no visible cause.
    // Only once the categories have loaded: before that (or after a failed
    // load) every category question would read as hidden, which is false.
    const hiddenCount = categoriesLoaded
        ? totalFaqs(faqs) - lists.reduce((sum, list) => sum + list.items.length, 0)
        : 0;

    const run = async (action: () => Promise<unknown>, successKey: string) => {
        try {
            await action();
            dispatch(enqueueSuccessSnackbar({ message: t(successKey), autoHideDuration: 3000 }));
            return true;
        } catch (err) {
            dispatch(enqueueErrorSnackbar({
                message: errorMessageFrom(err, t('services.faqs_error'), t),
                autoHideDuration: 6000,
            }));
            return false;
        }
    };

    const saveNew = async (category: string | undefined, question: TenantFaqText, answer: TenantFaqText) => {
        const saved = await run(
            () => addFaq({ ...(category ? { category } : {}), question, answer }).unwrap(),
            'services.faqs_saved',
        );
        if (saved) setEditing(null);
        return saved;
    };

    const saveEdit = async (id: string, question: TenantFaqText, answer: TenantFaqText) => {
        const saved = await run(() => updateFaq({ id, question, answer }).unwrap(), 'services.faqs_saved');
        if (saved) setEditing(null);
        return saved;
    };

    const confirmRemove = async (id: string) => {
        await run(() => removeFaq(id).unwrap(), 'services.faqs_removed');
        setConfirmingRemove(null);
    };

    const move = (list: FaqList, index: number, offset: -1 | 1) => {
        const ids = list.items.map((item) => item.id);
        const [moved] = ids.splice(index, 1);
        ids.splice(index + offset, 0, moved);
        void run(
            () => reorderFaqs({ ...(list.category ? { category: list.category } : {}), ids }).unwrap(),
            'services.faqs_saved',
        );
    };

    return (
        <section id={'faqs'} className={'bg-text-contrast rounded-[5px] shadow-sm p-6'}>
            <h2 className={'text-lg font-semibold text-foreground mb-1'}>
                {t('services.faqs_section')}
            </h2>
            <p className={'text-sm text-foreground/60'}>{t('services.faqs_desc')}</p>
            <p className={'text-sm text-foreground/60 mt-1 mb-2'}>{t('services.faqs_plain_text')}</p>

            {hiddenCount > 0 && (
                <p className={'text-sm text-foreground/60'}>
                    {t('services.faqs_hidden', { count: hiddenCount })}
                </p>
            )}

            {lists.map((list) => {
                const key = listKey(list.category);
                const sectionFull = list.items.length >= MAX_FAQS_PER_SECTION;
                return (
                    <div key={key} className={'flex flex-col gap-2 border-t border-foreground/5 pt-4 mt-4'}>
                        <h3 className={'text-sm font-semibold text-foreground'}>{list.label}</h3>

                        {list.items.length === 0 ? (
                            <p className={'text-sm text-foreground/60'}>{t('services.faqs_none')}</p>
                        ) : (
                            <ol className={'flex flex-col divide-y divide-foreground/5 text-sm'}>
                                {list.items.map((item, index) => {
                                    const question = questionOf(item, i18n.language);
                                    return (
                                        <li key={item.id} className={'py-2 flex flex-col gap-2'}>
                                            <div className={'flex items-start justify-between gap-3'}>
                                                <span className={'font-medium text-foreground'}>{question}</span>
                                                {!readOnly && list.items.length > 1 && (
                                                    <div className={'flex gap-1'}>
                                                        <button
                                                            type={'button'}
                                                            onClick={() => move(list, index, -1)}
                                                            disabled={busy || index === 0}
                                                            aria-label={t('services.faqs_move_up', { question })}
                                                            className={'p-1 text-foreground/60 disabled:opacity-30'}
                                                        >
                                                            <ArrowUp size={16} />
                                                        </button>
                                                        <button
                                                            type={'button'}
                                                            onClick={() => move(list, index, 1)}
                                                            disabled={busy || index === list.items.length - 1}
                                                            aria-label={t('services.faqs_move_down', { question })}
                                                            className={'p-1 text-foreground/60 disabled:opacity-30'}
                                                        >
                                                            <ArrowDown size={16} />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>

                                            {!readOnly && (
                                                <div className={'flex flex-wrap gap-x-4 gap-y-1'}>
                                                    <button
                                                        type={'button'}
                                                        onClick={() => setEditing({ kind: 'edit', id: item.id })}
                                                        disabled={busy}
                                                        className={'text-sm font-medium text-primary'}
                                                    >
                                                        {t('services.faqs_edit')}
                                                    </button>
                                                    <button
                                                        type={'button'}
                                                        onClick={() => setConfirmingRemove(item.id)}
                                                        disabled={busy}
                                                        className={'text-sm font-medium text-foreground/70'}
                                                    >
                                                        {t('services.faqs_remove')}
                                                    </button>
                                                </div>
                                            )}

                                            {confirmingRemove === item.id && (
                                                <div className={'flex flex-col gap-2 border border-foreground/15'
                                                    + ' rounded-[5px] p-3'}
                                                >
                                                    <p className={'text-sm text-foreground'} role={'alert'}>
                                                        {t('services.faqs_remove_confirm', { question })}
                                                    </p>
                                                    <div className={'flex flex-wrap gap-2'}>
                                                        <CustomButton
                                                            title={t('services.faqs_remove')}
                                                            onClick={() => confirmRemove(item.id)}
                                                            variant={'delete'}
                                                            isFilled={false}
                                                            disabled={busy}
                                                            loading={removing}
                                                        />
                                                        <CustomButton
                                                            title={t('common.cancel')}
                                                            onClick={() => setConfirmingRemove(null)}
                                                            isFilled={false}
                                                        />
                                                    </div>
                                                </div>
                                            )}

                                            {editing?.kind === 'edit' && editing.id === item.id && (
                                                <FaqForm
                                                    initial={draftFrom(item)}
                                                    busy={busy}
                                                    onSave={(q, a) => saveEdit(item.id, q, a)}
                                                    onCancel={() => setEditing(null)}
                                                />
                                            )}
                                        </li>
                                    );
                                })}
                            </ol>
                        )}

                        {!readOnly && (sectionFull || totalFull) && (
                            <p className={'text-sm text-foreground/60'}>
                                {t(sectionFull ? 'services.faqs_section_full' : 'services.faqs_total_full')}
                            </p>
                        )}

                        {!readOnly && !sectionFull && !totalFull && (
                            editing?.kind === 'add' && editing.list === key ? (
                                <FaqForm
                                    initial={draftFrom()}
                                    busy={busy}
                                    onSave={(q, a) => saveNew(list.category, q, a)}
                                    onCancel={() => setEditing(null)}
                                />
                            ) : (
                                <div>
                                    <button
                                        type={'button'}
                                        onClick={() => setEditing({ kind: 'add', list: key })}
                                        disabled={busy}
                                        className={'text-sm font-medium text-primary'}
                                    >
                                        {t('services.faqs_add')}
                                    </button>
                                </div>
                            )
                        )}
                    </div>
                );
            })}
        </section>
    );
};

export default TenantFaqsSection;
